import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import CreateNewCommentBankDesktop from './CreateNewCommentBankDesktop';
import Toast from '../../Toast';
import { useCommentBanks } from '../../state/hooks';
import { createNewFeedbackBank } from '../../../service';
import useNavigationBlock from '../../../hooks/useNavigationBlock';
import GeneralPopup from '../../GeneralPopup';
import { getUserId } from '../../../userLocalDetails';
import { useProfile } from '../../state/hooks';
import { useUserStates } from '../../../hooks/useUserStates';
import { buildMetadata } from '../../../utils/subjects';
import { useSubjects } from '../../../hooks/useSubjects';

const getDefaultCommentBank = () => ({
  title: 'Untitled Comment Bank',
  smartComments: [
    {
      title: 'First feedback area',
      suggestions: ['First comment', 'Second comment'],
    },
  ],
  years: [],
  subjects: [],
  taskTypes: [],
  responseTypes: [],
  ownerId: getUserId(),
});

export default function CreateNewCommentBankRoot() {
  const {
    setIsNavigationBlocked,
    isShowNavigationBlockPopup,
    confirmNavigationChange,
    cancelNavigationPopup,
  } = useNavigationBlock();

  const history = useHistory();
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const { states: userStates } = useUserStates();
  const { rawSubjects: subjectsList } = useSubjects(userStates);
  const [commentBank, setCommentBank] = useState(getDefaultCommentBank);
  // Both Save and Publish controls share one create: ignore clicks while it is
  // in flight, and once it has succeeded send repeat clicks to the created bank.
  const savingRef = useRef(false);
  const createdPathRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);

  const { resetData: resetCommentBanks } = useCommentBanks();

  const handleTitleChange = (event) => {
    setIsNavigationBlocked(true);
    setCommentBank({ ...commentBank, title: event.target.value });
  };

  const UpdateSmartAnotationHandler = (smartComment, index) => {
    setIsNavigationBlocked(true);
    const newSmartComments = [...commentBank.smartComments];
    newSmartComments[index] = smartComment;
    setCommentBank({ ...commentBank, smartComments: newSmartComments });
  };

  const deleteAnnotationHandler = (index) => {
    setIsNavigationBlocked(true);
    const newSmartComments = commentBank.smartComments.filter(
      (_, i) => i !== index
    );
    setCommentBank({ ...commentBank, smartComments: newSmartComments });
  };

  const createSmartAnnotationHandler = (title = 'Untitled feedback area') => {
    setIsNavigationBlocked(true);
    const newSmartComment = {
      title,
      suggestions: ['First comment', 'Second comment'],
    };
    setCommentBank({
      ...commentBank,
      smartComments: [...commentBank.smartComments, newSmartComment],
    });
  };

  const reorderSmartCommentsHandler = (reorderedComments) => {
    setIsNavigationBlocked(true);
    setCommentBank({ ...commentBank, smartComments: reorderedComments });
  };

  const updateCommentBank = (updates) => {
    setIsNavigationBlocked(true);
    setCommentBank({ ...commentBank, ...updates });
  };

  // Helper to convert subjects to object format with {subjectCode, subjectName}
  const subjectsAsObjects = (subjects) => {
    if (!Array.isArray(subjects)) return [];
    return subjects.map((s) => {
      // If already in correct format
      if (typeof s === 'object' && s !== null && s.subjectCode && s.subjectName) {
        return s;
      }
      // If object but missing fields
      if (typeof s === 'object' && s !== null) {
        return {
          subjectCode: s.subjectCode || s.code || s,
          subjectName: s.subjectName || s.name || s.title || s
        };
      }
      // If string, look up in subjectsList
      const subjectObj = subjectsList.find((sub) =>
        (sub.subjectCode || sub.code) === s || (sub.subjectName || sub.name || sub.title) === s
      );
      if (subjectObj) {
        return {
          subjectCode: subjectObj.subjectCode || subjectObj.code || s,
          subjectName: subjectObj.subjectName || subjectObj.name || subjectObj.title || s
        };
      }
      return null;
    }).filter(Boolean);
  };

  const saveCommentBank = (settings = {}) => {
    const meta = commentBank?.metadata || {};
    const years = settings.years || meta.years || commentBank.years || [];
    const subjects = settings.subjects || meta.subjects || commentBank.subjects || [];
    const taskTypes = settings.taskTypes || meta.taskTypes || commentBank.taskTypes || [];
    const responseTypes = settings.responseTypes || meta.responseTypes || commentBank.responseTypes || [];

    const subjectsForApi = subjectsAsObjects(subjects);
    const metadata = buildMetadata(
      userStates,
      { years, subjects: subjectsForApi, taskTypes, responseTypes },
      subjectsList
    );

    const newCommentBank = {
      title: commentBank.title,
      smartComments: commentBank.smartComments,
      years,
      subjects: subjectsForApi,
      taskTypes,
      responseTypes,
      metadata,
    };

    if (years.length === 0 || subjects.length === 0) {
      toast(<Toast message="Please select at least one Year and one Subject" />);
      return;
    }
    if (!newCommentBank.title.trim()) {
      toast(<Toast message="Please add a title for your comment bank" />);
      return;
    }

    if (newCommentBank.smartComments.length === 0) {
      toast(<Toast message="Please add at least one feedback area" />);
      return;
    }

    if (createdPathRef.current) {
      setIsNavigationBlocked(false);
      history.push(createdPathRef.current);
      return;
    }
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);

    createNewFeedbackBank(newCommentBank)
      .then((response) => {
        // postApi resolves null after a failed POST (it has already toasted the
        // error), so only a response counts as a created bank.
        if (!response) return;

        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'commentBanks'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'commentBanks'] });
        resetCommentBanks();

        setIsNavigationBlocked(false);

        toast(<Toast message="Comment bank created" />);

        const selfLink = response?._links?.self?.href;
        createdPathRef.current = selfLink
          ? `/tools/commentbanks/${selfLink.split('/').pop()}`
          : '/tools/commentbanks';
        history.push(createdPathRef.current);
      })
      .catch((error) => {
        console.error('Error saving comment bank:', error);
        toast(<Toast message="Error saving comment bank" />);
      })
      .finally(() => {
        savingRef.current = false;
        setIsSaving(false);
      });
  };

  return (
    <>
      {isShowNavigationBlockPopup && (
        <GeneralPopup
          hidePopup={cancelNavigationPopup}
          title="Unsaved Changes"
          textContent="You have unsaved changes in your comment bank. If you leave this page, your changes will be lost. Do you want to continue?"
          buttonText="Leave Without Saving"
          confirmButtonAction={confirmNavigationChange}
          closeBtnText="Stay on Page"
          cancelPopup={cancelNavigationPopup}
        />
      )}
      <CreateNewCommentBankDesktop
        commentBank={commentBank}
        updateCommentBank={updateCommentBank}
        UpdateSmartAnotationHandler={UpdateSmartAnotationHandler}
        deleteAnnotationHandler={deleteAnnotationHandler}
        createSmartAnnotationHandler={createSmartAnnotationHandler}
        reorderSmartCommentsHandler={reorderSmartCommentsHandler}
        saveCommentBank={saveCommentBank}
        handleTitleChange={handleTitleChange}
        isUpdating={false}
        isSaving={isSaving}
        commentBankId="new"
      />
    </>
  );
}
